import {
  RelativePositionTypeEnums,
  type TourFrame,
  type TourStep,
} from '@carboncopyco/types';
import { useTourDispatch, useTourSelector } from '../../../../../hooks';
import {
  addStepWarnings,
  removeStepWarnings,
  resetStepWarnings,
  selectFramesWarnings,
} from '../../../../../store';
import type { StepWarning } from '../../../../../types/step';
import { useTourEditorPageProps } from '../../../TourEditorPageContext';

type CalculatedWarning = {
  frameId: TourFrame['id'];
  stepId: TourStep['stepId'];
  warning: StepWarning;
  isWarningActive: boolean;
};

const useFrameWarnings = () => {
  const { uuid } = useTourEditorPageProps();
  const dispatch = useTourDispatch();

  const framesWarnings = useTourSelector(selectFramesWarnings(uuid));

  const addWarnings = (warnings: CalculatedWarning[]) => {
    dispatch(
      addStepWarnings({
        warnings,
        uuid,
      })
    );
  };

  const removeWarnings = (warnings: CalculatedWarning[]) => {
    dispatch(
      removeStepWarnings({
        warnings,
        uuid,
      })
    );
  };

  const removeWarning = (
    frameId: TourFrame['id'],
    stepId: TourStep['stepId'],
    warning: StepWarning
  ) => {
    removeWarnings([{ frameId, stepId, warning, isWarningActive: false }]);
  };

  const calculateStepWarnings = (
    frameId: TourFrame['id'],
    step: TourStep,
    showTooltips: boolean
  ): CalculatedWarning => {
    const isPositionWarningActive =
      step.config.tooltip.balloon.layout.relation ===
        RelativePositionTypeEnums.page && !showTooltips;

    return {
      frameId,
      stepId: step.stepId,
      warning: 'position',
      isWarningActive: isPositionWarningActive,
    };
  };

  const calculateAndStoreStepWarning = (
    frameId: TourFrame['id'],
    step: TourStep,
    showTooltips: boolean
  ) => {
    const warning = calculateStepWarnings(frameId, step, showTooltips);
    warning.isWarningActive
      ? addWarnings([warning])
      : removeWarnings([warning]);
  };

  const recalculateWarnings = (frames: TourFrame[]) => {
    dispatch(resetStepWarnings({ uuid }));
    calculateAndStoreStepWarnings(frames);
  };

  const calculateAndStoreStepWarnings = (frames: TourFrame[]) => {
    const warnings: CalculatedWarning[] = [];

    frames?.forEach((frame, frameIndex) => {
      frame.steps?.forEach((step, stepIndex) => {
        // Skip calculation for last step
        if (
          frameIndex === frames.length - 1 &&
          stepIndex === frame.steps.length - 1
        ) {
          return;
        }

        warnings.push(calculateStepWarnings(frame.id, step, step.showTooltip));
      });
    });

    const warningsToAdd = warnings.filter((warning) => warning.isWarningActive);
    const warningsToRemove = warnings.filter(
      (warning) => !warning.isWarningActive
    );

    addWarnings(warningsToAdd);
    removeWarnings(warningsToRemove);
  };

  const recalculateWarningsAfterDragging = (
    oldLastFrame: TourFrame,
    newLastFrame: TourFrame,
    showTooltips: boolean
  ) => {
    const lastStepOfOldLastFrame =
      oldLastFrame.steps?.[oldLastFrame.steps.length - 1];

    if (lastStepOfOldLastFrame) {
      calculateAndStoreStepWarning(
        oldLastFrame.id,
        lastStepOfOldLastFrame,
        showTooltips
      );
    }

    const lastStepOfNewLastFrame =
      newLastFrame.steps?.[newLastFrame.steps.length - 1];

    if (lastStepOfNewLastFrame) {
      removeWarning(newLastFrame.id, lastStepOfNewLastFrame.stepId, 'position');
    }
  };

  return {
    framesWarnings,
    calculateAndStoreStepWarnings,
    calculateAndStoreStepWarning,
    removeWarning,
    recalculateWarnings,
    recalculateWarningsAfterDragging,
  };
};

export default useFrameWarnings;
